import type { ProjectionModel } from "./model";
import type {
  FertilityProjectionResult,
  ObservedValue,
  ProjectionEstimate,
  ProjectionInput,
  ProjectionStage,
  ProjectionStatus,
  ProjectionUncertainty,
  ProjectionModelMetadata,
  ProjectionPresentation,
  RateEstimate,
} from "./types";

export const CUMULATIVE_PLANNING_MODEL_METADATA: ProjectionModelMetadata = {
  id: "cumulative-fertility-goal-planning",
  version: "1.0.0",
  reviewStatus: "pending_clinical_review" as const,
  population:
    "Planning model that requires an explicitly reviewed per-unit live-birth rate",
  validationScope:
    "Scenario planning across inventory or treatment attempts; does not predict a guaranteed number of cycles",
  sourceIds: [],
  parameterSourceIds: [],
  supportedFeatureIds: [
    "age",
    "oocyte_inventory",
    "embryo_inventory",
    "cycle_counts",
    "goal_planning",
  ],
  supportedGoalKinds: [
    "probability_at_least_one_live_birth",
    "probability_at_least_n_live_births",
    "expected_stage_count",
  ],
  priority: 60,
  estimateClass: "personalized" as const,
};

export const CUMULATIVE_PLANNING_MODEL: ProjectionModel = {
  metadata: CUMULATIVE_PLANNING_MODEL_METADATA,
  project(input, now = new Date()) {
    const planning = input.planning;
    const generatedAt = now.toISOString();
    const normalizedInputs: Record<string, number | string | null> = {
      kind: input.kind,
      modality: planning?.modality ?? null,
      inventoryUnit: planning?.inventoryUnit ?? null,
      desiredLiveBirths: planning?.desiredLiveBirths?.value ?? null,
      availableInventory: planning?.availableInventory?.value ?? null,
      expectedInventoryPerAttempt:
        planning?.expectedInventoryPerAttempt?.value ?? null,
      priorAttempts: planning?.priorAttempts?.value ?? null,
      priorLiveBirths: planning?.priorLiveBirths?.value ?? null,
      maxAttempts: planning?.maxAttempts ?? null,
    };

    if (input.context?.consentState === "withheld") {
      return planningResult(
        input,
        generatedAt,
        normalizedInputs,
        [],
        [],
        "not_applicable",
        false,
        "Projection not calculated because projection consent was withheld.",
      );
    }

    if (!planning) {
      return planningResult(
        input,
        generatedAt,
        normalizedInputs,
        [],
        ["cumulative fertility planning inputs"],
        "insufficient_data",
        false,
      );
    }

    const missingInputs: string[] = [];
    const stages: ProjectionStage[] = [];
    const desired = validPositiveInteger(planning.desiredLiveBirths.value);
    const inventory = validNonNegativeInteger(
      planning.availableInventory?.value,
    );
    const priorAttempts = validNonNegativeInteger(
      planning.priorAttempts?.value,
    );
    const priorLiveBirths = validNonNegativeInteger(
      planning.priorLiveBirths?.value,
    );
    const rate = validRate(planning.perUnitLiveBirthRate);
    const unitsPerAttempt =
      planning.inventoryUnit === "treatment_attempts"
        ? 1
        : validPositive(planning.expectedInventoryPerAttempt?.value);
    const maxAttempts = validNonNegativeInteger(planning.maxAttempts);
    const requestedLiveBirths =
      input.goal?.kind === "probability_at_least_n_live_births"
        ? validPositiveInteger(input.goal.count)
        : desired;

    if (desired == null) missingInputs.push("desired number of live births");
    if (inventory == null) missingInputs.push("available planning inventory");
    if (priorAttempts == null && planning.priorAttempts) {
      missingInputs.push("valid prior treatment attempts");
    }
    if (priorLiveBirths == null && planning.priorLiveBirths) {
      missingInputs.push("valid prior live births");
    }
    if (requestedLiveBirths == null) {
      missingInputs.push("valid live-birth target");
    }
    if (rate == null) {
      missingInputs.push("reviewed per-unit live-birth rate");
    }
    if (unitsPerAttempt == null) {
      missingInputs.push("expected inventory per treatment attempt");
    }
    if (maxAttempts == null && planning.maxAttempts != null) {
      missingInputs.push("valid maximum treatment attempts");
    }

    if (inventory != null) {
      stages.push(
        observedStage(
          planning.inventoryUnit === "mature_oocytes"
            ? "mature_oocytes"
            : planning.inventoryUnit === "euploid_embryos"
              ? "euploid_embryos"
              : planning.inventoryUnit === "transferable_embryos"
                ? "frozen_embryos"
                : "transfer_attempts",
          planning.availableInventory,
          inventory,
        ),
      );
    }

    const normalizedPriorAttempts = priorAttempts ?? 0;
    const normalizedPriorLiveBirths = priorLiveBirths ?? 0;
    const remainingBirths =
      requestedLiveBirths == null
        ? null
        : Math.max(0, requestedLiveBirths - normalizedPriorLiveBirths);
    const attempts =
      remainingBirths != null &&
      inventory != null &&
      rate &&
      unitsPerAttempt != null
        ? estimateAttempts(
            remainingBirths,
            inventory,
            unitsPerAttempt,
            rate,
            maxAttempts ?? undefined,
            normalizedPriorAttempts,
          )
        : null;

    if (attempts) {
      stages.push({
        id:
          planning.modality === "egg_freezing" ||
          planning.modality === "embryo_freezing"
            ? "retrieval_cycles"
            : "transfer_attempts",
        basis: "modeled",
        estimate: attempts,
        sourceIds: rate?.sourceIds ?? [],
      });
    }

    const probability =
      remainingBirths != null &&
      inventory != null &&
      rate &&
      unitsPerAttempt != null &&
      attempts
        ? cumulativeProbability(
            inventory,
            unitsPerAttempt,
            normalizedPriorAttempts,
            attempts,
            remainingBirths,
            rate,
          )
        : null;
    if (probability && rate) {
      stages.push({
        id: "live_births",
        basis: "modeled",
        estimate: probability,
        rate,
        sourceIds: rate.sourceIds,
      });
    }

    const plannedUnits =
      remainingBirths != null &&
      inventory != null &&
      rate &&
      unitsPerAttempt != null &&
      attempts
        ? plannedUnitEstimate(
            inventory,
            unitsPerAttempt,
            normalizedPriorAttempts,
            attempts,
          )
        : null;
    const modeled = stages.some((stage) => stage.basis === "modeled");
    const status: ProjectionStatus =
      stages.length === 0
        ? "insufficient_data"
        : missingInputs.length > 0
          ? "partial"
          : modeled
            ? "not_reviewed"
            : "available";

    return {
      ...planningResult(
        input,
        generatedAt,
        normalizedInputs,
        stages,
        missingInputs,
        status,
        modeled,
      ),
      probabilityAtLeastOneLiveBirth:
        remainingBirths != null &&
        inventory != null &&
        rate &&
        unitsPerAttempt != null &&
        attempts
          ? cumulativeProbability(
              inventory,
              unitsPerAttempt,
              normalizedPriorAttempts,
              attempts,
              Math.max(0, 1 - normalizedPriorLiveBirths),
              rate,
            )
          : null,
      probabilityAtLeastNLiveBirths: probability,
      expectedLiveBirths:
        plannedUnits && rate
          ? expectedCount(plannedUnits, rate, normalizedPriorLiveBirths)
          : null,
    };
  },
};

function planningResult(
  input: ProjectionInput,
  generatedAt: string,
  normalizedInputs: Record<string, number | string | null>,
  stages: ProjectionStage[],
  missingInputs: string[],
  status: ProjectionStatus,
  modeled: boolean,
  extraWarning?: string,
): FertilityProjectionResult {
  const presentation = planningPresentation(input, modeled);
  const sourceIds = [
    ...(input.evidence?.map((source) => source.id) ?? []),
    ...observedSourceIds(input),
    ...(input.planning?.perUnitLiveBirthRate?.sourceIds ?? []),
  ];
  return {
    contractVersion: "1",
    status,
    presentation,
    kind: input.kind,
    model: CUMULATIVE_PLANNING_MODEL_METADATA,
    generatedAt,
    asOf: input.asOf ?? null,
    normalizedInputs,
    missingInputs: [...new Set(missingInputs)],
    assumptions: [
      "Planning targets describe scenarios and expected effort; they are not clinical recommendations or guarantees.",
      "Cumulative calculations assume each modeled unit has the supplied rate and use the rate range as a planning interval.",
      "Observed prior live births reduce the remaining target but do not change the supplied per-unit rate.",
    ],
    warnings: [
      ...(presentation === "general_baseline"
        ? [
            "This cumulative scenario uses a general baseline from population-level evidence and is not clinic-specific.",
          ]
        : modeled
          ? [
              "The cumulative planning adapter is pending clinical/product review and must remain gated until the selected rate is approved.",
            ]
          : []),
      ...(extraWarning ? [extraWarning] : []),
    ],
    stages,
    uncertainty: planningUncertainty(),
    expectedLiveBirths: null,
    probabilityAtLeastOneLiveBirth: null,
    probabilityAtLeastNLiveBirths: null,
    sourceIds: [...new Set(sourceIds)],
    disclaimer:
      "This scenario is for planning only and is not a diagnosis, treatment recommendation, or guarantee of pregnancy or live birth.",
  };
}

const GENERAL_BASELINE_SOURCE_IDS = new Set([
  "goldman-oocyte-cryopreservation-2017",
  "asrm-embryo-vitrification-2021",
  "tummon-frozen-thawed-embryo-transfer-2006",
]);

function planningPresentation(
  input: ProjectionInput,
  modeled: boolean,
): ProjectionPresentation {
  if (!modeled) return "patient_safe";
  const sourceIds = input.planning?.perUnitLiveBirthRate?.sourceIds ?? [];
  if (
    sourceIds.length > 0 &&
    sourceIds.every((sourceId) => GENERAL_BASELINE_SOURCE_IDS.has(sourceId))
  ) {
    return "general_baseline";
  }
  return "blocked_pending_review";
}

function estimateAttempts(
  remainingBirths: number,
  availableInventory: number,
  unitsPerAttempt: number,
  rate: RateEstimate,
  maxAttempts?: number,
  priorAttempts = 0,
): ProjectionEstimate {
  const attempts = (probability: number) => {
    const expectedFromAvailable = availableInventory * probability;
    const expectedRemaining = Math.max(
      0,
      remainingBirths - expectedFromAvailable,
    );
    const additional = Math.ceil(
      expectedRemaining /
        Math.max(Number.EPSILON, unitsPerAttempt * probability),
    );
    const total = priorAttempts + additional;
    return maxAttempts == null
      ? total
      : Math.max(priorAttempts, Math.min(total, maxAttempts));
  };
  const low = attempts(rate.high);
  const mean = attempts(rate.mean);
  const high = attempts(rate.low);
  return {
    mean,
    low,
    high,
    unit: "count",
  };
}

function cumulativeProbability(
  availableInventory: number,
  unitsPerAttempt: number,
  priorAttempts: number,
  attempts: ProjectionEstimate,
  target: number,
  rate: RateEstimate,
): ProjectionEstimate {
  return {
    mean: binomialAtLeast(
      plannedUnitCount(
        availableInventory,
        unitsPerAttempt,
        priorAttempts,
        attempts.mean,
      ),
      rate.mean,
      target,
    ),
    low: binomialAtLeast(
      plannedUnitCount(
        availableInventory,
        unitsPerAttempt,
        priorAttempts,
        attempts.high,
      ),
      rate.low,
      target,
    ),
    high: binomialAtLeast(
      plannedUnitCount(
        availableInventory,
        unitsPerAttempt,
        priorAttempts,
        attempts.low,
      ),
      rate.high,
      target,
    ),
    unit: "probability",
  };
}

function binomialAtLeast(
  rawUnits: number,
  probability: number,
  target: number,
) {
  const units = Math.max(0, Math.round(rawUnits));
  if (target > units) return 0;
  if (target <= 0) return 1;
  if (probability <= 0) return 0;
  if (probability >= 1) return 1;

  // Sum the lower tail using a recurrence. This avoids factorial overflow and
  // only requires target terms, which is the stable side for small family
  // goals such as one or two live births.
  let mass = Math.pow(1 - probability, units);
  let lowerTail = mass;
  for (let successes = 0; successes < target - 1; successes += 1) {
    mass *=
      ((units - successes) / (successes + 1)) *
      (probability / (1 - probability));
    lowerTail += mass;
  }
  return clamp(1 - lowerTail, 0, 1);
}

function plannedUnitCount(
  availableInventory: number,
  unitsPerAttempt: number,
  priorAttempts: number,
  totalAttempts: number,
): number {
  return (
    availableInventory +
    Math.max(0, totalAttempts - priorAttempts) * unitsPerAttempt
  );
}

function plannedUnitEstimate(
  availableInventory: number,
  unitsPerAttempt: number,
  priorAttempts: number,
  attempts: ProjectionEstimate,
): ProjectionEstimate {
  return {
    mean: plannedUnitCount(
      availableInventory,
      unitsPerAttempt,
      priorAttempts,
      attempts.mean,
    ),
    low: plannedUnitCount(
      availableInventory,
      unitsPerAttempt,
      priorAttempts,
      attempts.low,
    ),
    high: plannedUnitCount(
      availableInventory,
      unitsPerAttempt,
      priorAttempts,
      attempts.high,
    ),
    unit: "count",
  };
}

function expectedCount(
  units: ProjectionEstimate,
  rate: RateEstimate,
  priorLiveBirths: number,
): ProjectionEstimate {
  return {
    mean: priorLiveBirths + units.mean * rate.mean,
    low: priorLiveBirths + units.mean * rate.low,
    high: priorLiveBirths + units.mean * rate.high,
    unit: "count",
  };
}

function observedStage(
  id: string,
  observed: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "observed",
    observed,
    estimate: { mean: value, low: value, high: value, unit: "count" },
    sourceIds: observed?.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
  };
}

function validPositive(value: number | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
}

function validPositiveInteger(value: number | undefined): number | null {
  return value != null && Number.isInteger(value) && value > 0 ? value : null;
}

function validNonNegativeInteger(value: number | undefined): number | null {
  return value != null && Number.isInteger(value) && value >= 0 ? value : null;
}

function validRate(rate: RateEstimate | undefined): RateEstimate | null {
  if (!rate) return null;
  if (
    ![rate.mean, rate.low, rate.high].every((value) => Number.isFinite(value))
  ) {
    return null;
  }
  if (
    rate.mean < 0 ||
    rate.mean > 1 ||
    rate.low < 0 ||
    rate.low > 1 ||
    rate.high < 0 ||
    rate.high > 1 ||
    rate.low > rate.mean ||
    rate.high < rate.mean
  ) {
    return null;
  }
  return {
    ...rate,
    low: Math.min(rate.low, rate.mean),
    high: Math.max(rate.high, rate.mean),
  };
}

function planningUncertainty(): ProjectionUncertainty {
  return {
    kind: "interval",
    method: "rate-range-propagation",
    note: "The interval propagates the supplied reviewed per-unit rate range; it is not a patient-specific confidence interval.",
  };
}

function observedSourceIds(input: ProjectionInput): string[] {
  const values = [
    input.planning?.desiredLiveBirths,
    input.planning?.availableInventory,
    input.planning?.expectedInventoryPerAttempt,
    input.planning?.priorAttempts,
    input.planning?.priorLiveBirths,
  ];
  return values.flatMap((value) =>
    value?.provenance.sourceId ? [value.provenance.sourceId] : [],
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
